import { store, game } from "../state/index.ts";
import { Chess } from "#vendor/chessjs/chess.js";
import { K } from "../state/keys.ts";
import { buildUciHistory, pvUciToSan } from "./utils.js";
import * as api from "../services/api.ts";
import * as storage from "../services/storage.ts";
import { updateBookOptions, applyMove } from "./board.js";
import { deviationAt, moverFrom } from "../domain/repertoireDeviation.ts";

// DOM elements. Managing the repertoires — the list, the import — is the /repertoire page, and
// that page is a component now: what is left here is what the board needs while practising.
let repertoireDetails = null;
let repertoireSummary = null;
let repertoireNextMovesEl = null;

// State
let isLoggedIn = false;
let repertoires = [];
let repertoireSuccessCount = 0;
let lastCheckedMoveCount = 0;

// Only the answer for the position currently on the board may be applied
let checkToken = 0;

// What the repertoire said of every position this game went through, by the moves that reached it: who left the
// repertoire is read off it (domain/repertoireDeviation.ts). The same moves from the same start are the same
// position, so the answers stay good until the repertoires change.
const checked = new Map();
const checkKey = (baseFen, uciMoves) => `${baseFen || ""}|${uciMoves.join(",")}`;

export function initRepertoire(elements) {
    repertoireDetails = elements.repertoireDetails;
    repertoireSummary = elements.repertoireSummary;
    repertoireNextMovesEl = elements.repertoireNextMovesEl;

    repertoireDetails?.addEventListener("toggle", () => {
        persistRepertoireCollapseState();
        updateRepertoireSummary();
    });
}

export function setLoggedIn(loggedIn) {
    isLoggedIn = loggedIn;
    if (loggedIn) {
        loadRepertoiresList();
    } else {
        repertoires = [];
    }
}

export function updateRepertoireSummary() {
    if (!repertoireSummary) return;
    const activeCount = repertoires.filter((r) => r.active).length;
    if (activeCount === 0) {
        repertoireSummary.textContent = "No active repertoires";
    } else if (activeCount === 1) {
        const active = repertoires.find((r) => r.active);
        repertoireSummary.textContent = active.name;
    } else {
        repertoireSummary.textContent = `${activeCount} active repertoires`;
    }
}

function persistRepertoireCollapseState() {
    if (!repertoireDetails) return;
    storage.saveRepertoireCollapseState(repertoireDetails.open ? "open" : "closed");
}

export function restoreRepertoireCollapseState() {
    if (!repertoireDetails) return;
    const state = storage.loadRepertoireCollapseState();
    if (state === "closed") {
        repertoireDetails.open = false;
    }
}

export async function loadRepertoiresList() {
    if (!isLoggedIn) {
        repertoires = [];
        return;
    }

    try {
        repertoires = await api.fetchRepertoires();
        checked.clear();
        updateRepertoireSummary();
        checkRepertoire();
    } catch (err) {
        console.error("Failed to load repertoires", err);
        repertoires = [];
    }
}

export function refreshRepertoireEngineHint() {
    // Engine hint is no longer displayed in the global status card
    // This function is kept for backward compatibility but does nothing now
}

function renderRepertoireStatus(state) {
    const debugOn = store.get('config.debugOn');
    if (debugOn) {
        console.log("renderRepertoireStatus", state);
    }

    // Write status to store - subscribers (quickStatus, globalStatus) react
    store.batch((set) => {
        set('repertoire.status', state ? { ...state } : null);
        set('repertoire.fen', state?.fen || null);
    });

    // Always update store so subscribers (quickStatus) get the moves
    const moves = state?.nextMoves || [];
    store.set('repertoire.latestMoves', Array.isArray(moves) ? moves : []);

    // Render next moves in the repertoire card
    if (!repertoireNextMovesEl) return;
    repertoireNextMovesEl.innerHTML = "";
    if (!moves.length) {
        updateBookOptions();
        return;
    }
    moves.forEach((mv) => {
        const san = pvUciToSan(mv, game.fen());

        const li = document.createElement("li");
        const div = document.createElement("div");
        const span = document.createElement("span");
        span.className = "move-text";
        span.textContent = san;
        div.appendChild(span);
        div.className = "move-line";
        li.appendChild(div);

        li.addEventListener("click", () => applyMove({ uci: mv, san: san }));

        repertoireNextMovesEl.appendChild(li);
    });
    updateBookOptions();
}

export async function checkRepertoire() {
    const hasActive = repertoires.some((r) => r.active);
    if (!hasActive) {
        repertoireSuccessCount = 0;
        lastCheckedMoveCount = 0;
        // The fen travels with the answer: the practice only quotes the repertoire when the answer
        // is about the position on the board, and "there is no repertoire" is an answer too.
        renderRepertoireStatus({
            message: "No active repertoire.", noRepertoire: true, nextMoves: [], successCount: 0, fen: game.fen(),
        });
        return;
    }

    const fen = game.fen();
    const token = ++checkToken;
    const historyMoves = buildUciHistory();
    const currentMoveCount = historyMoves.length;
    const params = new URLSearchParams();
    if (historyMoves.length) {
        params.set("moves", historyMoves.join(","));
    }
    const baseFen = store.get(K.boards("study").baseFen);
    if (baseFen) {
        params.set("fen", baseFen);
    }
    try {
        const data = await api.fetchRepertoireNext(params);
        // The board moved on while waiting: these are the moves of another position
        if (token !== checkToken || game.fen() !== fen) return;
        data.fen = fen;

        // Track successful repertoire moves
        if (currentMoveCount > lastCheckedMoveCount && data.inRepertoire) {
            // A new move was made and we're still in repertoire - count it as success
            repertoireSuccessCount++;
        } else if (currentMoveCount < lastCheckedMoveCount) {
            // User went back in history - recalculate count based on current position
            // For simplicity, we reset and count based on whether we're in repertoire
            if (!data.inRepertoire) {
                repertoireSuccessCount = Math.max(0, repertoireSuccessCount - (lastCheckedMoveCount - currentMoveCount));
            }
        } else if (currentMoveCount === 0) {
            // Game reset
            repertoireSuccessCount = 0;
        }

        lastCheckedMoveCount = currentMoveCount;
        data.successCount = repertoireSuccessCount;

        // Who left the repertoire: whoever made the move after the last position that was in it, and not whoever
        // happened to move last — the rival answering a move of yours out of the book did not leave it
        const userSide = store.get('board.userSide') || 'white';
        const currentTurn = game.turn(); // whose turn it is NOW (after the last move)
        const isUserTurn = (userSide === 'white' && currentTurn === 'w') || (userSide === 'black' && currentTurn === 'b');

        if (!data.noRepertoire) {
            checked.set(checkKey(baseFen, historyMoves), {
                inRepertoire: !!data.inRepertoire, nextMoves: data.nextMoves || [], fen,
            });
        }
        if (!data.inRepertoire && !data.noRepertoire && currentMoveCount > 0) {
            // A line comes onto the board several moves at once: the positions it went through were never asked
            // about, and the move that left the repertoire may be any of them
            await fillGaps(baseFen, historyMoves);
            if (token !== checkToken || game.fen() !== fen) return;
            const checks = historyMoves.map((_, k) => checked.get(checkKey(baseFen, historyMoves.slice(0, k))));
            checks.push(checked.get(checkKey(baseFen, historyMoves)));
            const deviation = deviationAt(checks, game.history(), currentMoveCount,
                moverFrom(currentTurn, currentMoveCount, userSide));
            data.deviatedBy = deviation?.by ?? null;
            if (deviation) {
                data.deviation = {
                    ...deviation,
                    expectedSans: deviation.expected.map((pv) => pvUciToSan(pv.trim().split(/\s+/)[0], deviation.expectedFen, 1)),
                };
            }
        }

        // End of repertoire: in repertoire, no next moves, and it's the user's turn
        if (data.inRepertoire && (!data.nextMoves || data.nextMoves.length === 0) && isUserTurn) {
            data.endOfRepertoire = true;
        }

        renderRepertoireStatus(data);
    } catch (err) {
        if (token !== checkToken) return;
        renderRepertoireStatus({ message: `Repertoire check failed: ${err.message}`, nextMoves: [], successCount: repertoireSuccessCount });
    }
}

/** Asks about the positions of this game never asked about, back to the last one known to be in the repertoire. */
async function fillGaps(baseFen, historyMoves) {
    const missing = [];
    for (let k = historyMoves.length - 1; k >= 0 && missing.length < 40; k--) {
        const known = checked.get(checkKey(baseFen, historyMoves.slice(0, k)));
        if (known?.inRepertoire) break;
        if (!known) missing.push(k);
    }
    await Promise.all(missing.map(async (k) => {
        const prefix = historyMoves.slice(0, k);
        const params = new URLSearchParams();
        if (prefix.length) params.set("moves", prefix.join(","));
        if (baseFen) params.set("fen", baseFen);
        const answer = await api.fetchRepertoireNext(params);
        const replay = new Chess(baseFen || undefined);
        prefix.forEach((uci) => replay.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] }));
        checked.set(checkKey(baseFen, prefix), {
            inRepertoire: !!answer.inRepertoire, nextMoves: answer.nextMoves || [], fen: replay.fen(),
        });
    }));
}

export function resetRepertoireCount() {
    repertoireSuccessCount = 0;
    lastCheckedMoveCount = 0;
}
